import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

const en = {
  'app.brand': 'ConnectDevice',
  'app.platform': 'IoT Battery Management Platform',
  'app.shortPlatform': 'IoT Battery Management',
  'app.localDevelopment': 'Local Development',
  'app.localEnvironment': 'Local development environment',
  'app.demoClient': 'ConnectDevice Demo',
  'app.notAvailableYet': '{label} is not available yet',

  'language.label': 'Language',
  'language.english': 'English',
  'language.portuguese': 'Portuguese',
  'language.spanish': 'Spanish',

  'nav.dashboard': 'Dashboard',
  'nav.liveTesting': 'Live Testing',
  'nav.batchMgmt': 'Batch Mgmt',
  'nav.labTools': 'Lab Tools',
  'nav.disposition': 'Disposition',
  'nav.analytics': 'Analytics',
  'nav.admin': 'Admin',
  'nav.overview': 'Overview',
  'nav.testing': 'Testing',
  'nav.lots': 'Lots',
  'nav.equipment': 'Equipment',
  'nav.reports': 'Reports',
  'nav.support': 'Support',
  'nav.logout': 'Logout',
  'nav.scanNewSerial': 'Scan New Serial',
  'nav.notifications': 'Notifications',
  'nav.settings': 'Settings',

  'pageTitle.batteryTests': 'Battery Test Logs',
  'pageTitle.batteryModels': 'Battery Model Library',
  'pageTitle.dashboard': 'Dashboard',

  'auth.loading': 'Loading console...',
  'auth.signIn': 'Sign in',
  'auth.signingIn': 'Signing in...',
  'auth.username': 'Username',
  'auth.password': 'Password',
  'auth.invalidCredentials': 'Invalid username or password.',
  'auth.validation.usernameRequired': 'Username is required',
  'auth.validation.passwordRequired': 'Password is required',
  'auth.sessionExpired': 'Your session has expired. Please log in again.',
  'auth.operatorFallback': 'Operator',

  'status.online': 'ONLINE',
  'status.offline': 'OFFLINE',
  'status.good': 'GOOD',
  'status.half': 'HALF',
  'status.defect': 'DEFECT',
  'status.recarga': 'RECHARGE',
  'status.approved': 'APPROVED',
  'status.failed': 'FAILED',
  'status.retest': 'RETEST',
  'status.recharge': 'RECHARGE',
  'status.idle': 'IDLE',
  'status.analyzing': 'ANALYZING',

  'dashboard.title': 'Lab Overview Dashboard',
  'dashboard.subtitle': 'Real-time battery quality intelligence and equipment telemetry.',
  'dashboard.currentLoad': 'Current Load: 84%',
  'dashboard.systemsNominal': 'Systems Nominal',
  'dashboard.totalTests': 'Total Tests (24h)',
  'dashboard.totalTestsTrend': '+12% vs LW',
  'dashboard.passRate': 'Pass Rate (GOOD %)',
  'dashboard.passRateTrend': 'Target: 98%',
  'dashboard.processingCount': 'Processing Count',
  'dashboard.processingTrend': 'Active queue',
  'dashboard.onlineEquipment': 'Online Equipment',
  'dashboard.onlineEquipmentTrend': '93.7% active',
  'dashboard.throughput': '24h Battery Throughput',
  'dashboard.hourly': 'Hourly',
  'dashboard.daily': 'Daily',
  'dashboard.loadingTelemetry': 'Loading telemetry...',
  'dashboard.yieldSegmentation': 'Yield Segmentation',
  'dashboard.goodLabel': 'GOOD',
  'dashboard.halfLabel': 'HALF (B-Grade)',
  'dashboard.defectLabel': 'DEFECT (Scrap)',
  'dashboard.recargaLabel': 'RECARGA (Retest)',
  'dashboard.traceability': 'Traceability ensured for all 4 classifications.',
  'dashboard.stationSlots': 'Station Slots',
  'dashboard.active': 'Active',
  'dashboard.done': 'Done',
  'dashboard.fault': 'Fault',
  'dashboard.linkedHardware': 'Linked Hardware',
  'dashboard.activeStation': 'Active station',
  'dashboard.maintenanceQueue': 'Maintenance queue',
  'dashboard.recentAnalyses': 'Recent Battery Analyses',
  'dashboard.recentAnalysesSubtitle': 'Showing latest traceable quality results',
  'dashboard.viewAllLogs': 'View All Logs',
  'dashboard.serialNumber': 'Serial Number',
  'dashboard.healthSoh': 'Health (SOH)',
  'dashboard.timestamp': 'Timestamp',
  'dashboard.liveEquipmentStatus': 'Live Equipment Status',
  'dashboard.viewFullAnalytics': 'View Full Analytics',
  'dashboard.livePerformanceHeatmap': 'Live Performance Heatmap',
  'dashboard.heatmapDescription': 'Thermal and voltage fluctuations detected in local demo equipment. One unassigned slot shows high resistance anomalies.',
  'dashboard.temp': 'Temp',
  'dashboard.volt': 'Volt',
  'dashboard.anomalyQueue': 'Anomaly Detection Queue',
  'dashboard.voltageSag': 'Voltage Sag Detected',
  'dashboard.capacitanceOutOfRange': 'Capacitance Out of Range',
  'dashboard.minorThermalDrift': 'Minor Thermal Drift',
  'dashboard.runDiagnostics': 'Run System Diagnostics',
  'dashboard.analyse': 'Analyse',
  'dashboard.currentLot': 'Current Lot',
  'dashboard.lastActive': 'Last Active',
  'dashboard.lastActiveDemo': '2h 14m ago',
  'dashboard.runtime': 'Runtime',
  'dashboard.system': 'SYSTEM',
  'dashboard.slot': 'SLOT',
  'dashboard.throughputCounters': 'Throughput Counters',
  'dashboard.proc': 'PROC',
  'dashboard.fail': 'FAIL',
  'dashboard.slotGridStatus': 'Slot Grid Status (16)',
  'dashboard.assignLot': 'Assign Lot',
  'dashboard.modeConfig': 'Mode Config',
  'dashboard.none': 'NONE',
  'dashboard.unassigned': 'Unassigned',

  'models.title': 'Battery Model Library',
  'models.subtitle': 'Capacity, voltage, and quality thresholds used for lab classification.',
  'models.closeLot': 'Close Lot',
  'models.newModel': 'New Model',
  'models.configuredModels': 'Configured Models',
  'models.approvalThresholds': 'Approval Thresholds',
  'models.environment': 'Environment',
  'models.local': 'Local',
  'models.loading': 'Loading battery models...',
  'models.loadError': 'Failed to load battery models.',
  'models.batteryModel': 'Battery model',
  'models.voltage': 'Voltage',
  'models.capacity': 'Capacity',
  'models.approvalThreshold': 'Approval threshold',
  'models.rejectionThreshold': 'Rejection threshold',
  'models.traceabilityConfig': 'Traceability config',

  'reports.title': 'Battery Test Logs',
  'reports.subtitle': 'Traceable test results, slot history, and exportable quality records.',
  'reports.searchPlaceholder': 'Search global serial number...',
  'reports.dateRange': 'Date Range',
  'reports.datePlaceholder': 'mm/dd/yyyy',
  'reports.startDate': 'Start date',
  'reports.endDate': 'End date',
  'reports.status': 'Status',
  'reports.allStatuses': 'All Statuses',
  'reports.equipment': 'Equipment',
  'reports.allEquipment': 'Any equipment',
  'reports.client': 'Client',
  'reports.exportCsv': 'Export CSV',
  'reports.applyFilters': 'Apply Filters',
  'reports.clearFilters': 'Clear Filters',
  'reports.loading': 'Loading test records...',
  'reports.loadError': 'Failed to load test results.',
  'reports.empty': 'No test records match the selected filters.',
  'reports.serial': 'Serial',
  'reports.model': 'Model',
  'reports.demoPack': 'Demo Pack 2500mAh',
  'reports.measured': 'Measured',
  'reports.configured': 'Configured',
  'reports.health': 'Health %',
  'reports.internalResistance': 'IR (mOhm)',
  'reports.showing': 'Showing',
  'reports.of': 'of',
  'reports.entries': 'entries',
  'reports.previous': 'Previous',
  'reports.next': 'Next',
  'reports.notAvailable': 'N/A',
};

export type TranslationKey = keyof typeof en;
export type Language = 'pt-BR' | 'en' | 'es';

const pt: Record<TranslationKey, string> = {
  'app.brand': 'ConnectDevice',
  'app.platform': 'Plataforma IoT de Gerenciamento de Baterias',
  'app.shortPlatform': 'Gerenciamento IoT de Baterias',
  'app.localDevelopment': 'Desenvolvimento Local',
  'app.localEnvironment': 'Ambiente de desenvolvimento local',
  'app.demoClient': 'Demonstração ConnectDevice',
  'app.notAvailableYet': '{label} ainda não está disponível',

  'language.label': 'Idioma',
  'language.english': 'Inglês',
  'language.portuguese': 'Português',
  'language.spanish': 'Espanhol',

  'nav.dashboard': 'Painel',
  'nav.liveTesting': 'Teste ao Vivo',
  'nav.batchMgmt': 'Gestão de Lotes',
  'nav.labTools': 'Ferramentas do Lab',
  'nav.disposition': 'Disposição',
  'nav.analytics': 'Análises',
  'nav.admin': 'Admin',
  'nav.overview': 'Visão Geral',
  'nav.testing': 'Testes',
  'nav.lots': 'Lotes',
  'nav.equipment': 'Equipamentos',
  'nav.reports': 'Relatórios',
  'nav.support': 'Suporte',
  'nav.logout': 'Sair',
  'nav.scanNewSerial': 'Escanear Novo Serial',
  'nav.notifications': 'Notificações',
  'nav.settings': 'Configurações',

  'pageTitle.batteryTests': 'Registros de Testes de Bateria',
  'pageTitle.batteryModels': 'Biblioteca de Modelos de Bateria',
  'pageTitle.dashboard': 'Painel',

  'auth.loading': 'Carregando console...',
  'auth.signIn': 'Entrar',
  'auth.signingIn': 'Entrando...',
  'auth.username': 'Usuário',
  'auth.password': 'Senha',
  'auth.invalidCredentials': 'Usuário ou senha inválidos.',
  'auth.validation.usernameRequired': 'Usuário é obrigatório',
  'auth.validation.passwordRequired': 'Senha é obrigatória',
  'auth.sessionExpired': 'Sua sessão expirou. Entre novamente.',
  'auth.operatorFallback': 'Operador',

  'status.online': 'ONLINE',
  'status.offline': 'OFFLINE',
  'status.good': 'BOM',
  'status.half': 'MEIA VIDA',
  'status.defect': 'DEFEITO',
  'status.recarga': 'RECARGA',
  'status.approved': 'APROVADO',
  'status.failed': 'FALHOU',
  'status.retest': 'RETESTE',
  'status.recharge': 'RECARGA',
  'status.idle': 'OCIOSO',
  'status.analyzing': 'ANALISANDO',

  'dashboard.title': 'Painel Geral do Laboratório',
  'dashboard.subtitle': 'Inteligência em tempo real de qualidade de baterias e telemetria de equipamentos.',
  'dashboard.currentLoad': 'Carga Atual: 84%',
  'dashboard.systemsNominal': 'Sistemas Nominais',
  'dashboard.totalTests': 'Total de Testes (24h)',
  'dashboard.totalTestsTrend': '+12% vs semana passada',
  'dashboard.passRate': 'Taxa de Aprovação (BOM %)',
  'dashboard.passRateTrend': 'Meta: 98%',
  'dashboard.processingCount': 'Em Processamento',
  'dashboard.processingTrend': 'Fila ativa',
  'dashboard.onlineEquipment': 'Equipamentos Online',
  'dashboard.onlineEquipmentTrend': '93,7% ativos',
  'dashboard.throughput': 'Vazão de Baterias em 24h',
  'dashboard.hourly': 'Por Hora',
  'dashboard.daily': 'Diário',
  'dashboard.loadingTelemetry': 'Carregando telemetria...',
  'dashboard.yieldSegmentation': 'Segmentação de Rendimento',
  'dashboard.goodLabel': 'BOM',
  'dashboard.halfLabel': 'MEIA VIDA (Categoria B)',
  'dashboard.defectLabel': 'DEFEITO (Sucata)',
  'dashboard.recargaLabel': 'RECARGA (Reteste)',
  'dashboard.traceability': 'Rastreabilidade garantida para todas as 4 classificações.',
  'dashboard.stationSlots': 'Slots da Estação',
  'dashboard.active': 'Ativo',
  'dashboard.done': 'Concluído',
  'dashboard.fault': 'Falha',
  'dashboard.linkedHardware': 'Hardware Vinculado',
  'dashboard.activeStation': 'Estação ativa',
  'dashboard.maintenanceQueue': 'Fila de manutenção',
  'dashboard.recentAnalyses': 'Análises Recentes de Bateria',
  'dashboard.recentAnalysesSubtitle': 'Exibindo os resultados de qualidade rastreáveis mais recentes',
  'dashboard.viewAllLogs': 'Ver Todos os Registros',
  'dashboard.serialNumber': 'Número de Série',
  'dashboard.healthSoh': 'Saúde (SOH)',
  'dashboard.timestamp': 'Data/Hora',
  'dashboard.liveEquipmentStatus': 'Status dos Equipamentos ao Vivo',
  'dashboard.viewFullAnalytics': 'Ver Análise Completa',
  'dashboard.livePerformanceHeatmap': 'Mapa de Calor de Performance ao Vivo',
  'dashboard.heatmapDescription': 'Oscilações térmicas e de tensão detectadas no equipamento de demonstração local. Um slot sem atribuição mostra anomalias de alta resistência.',
  'dashboard.temp': 'Temp',
  'dashboard.volt': 'Volt',
  'dashboard.anomalyQueue': 'Fila de Detecção de Anomalias',
  'dashboard.voltageSag': 'Queda de Tensão Detectada',
  'dashboard.capacitanceOutOfRange': 'Capacitância Fora da Faixa',
  'dashboard.minorThermalDrift': 'Pequeno Desvio Térmico',
  'dashboard.runDiagnostics': 'Executar Diagnóstico do Sistema',
  'dashboard.analyse': 'Analisar',
  'dashboard.currentLot': 'Lote Atual',
  'dashboard.lastActive': 'Última Atividade',
  'dashboard.lastActiveDemo': 'há 2h 14min',
  'dashboard.runtime': 'Tempo Ativo',
  'dashboard.system': 'SISTEMA',
  'dashboard.slot': 'SLOT',
  'dashboard.throughputCounters': 'Contadores de Vazão',
  'dashboard.proc': 'PROC',
  'dashboard.fail': 'FALHA',
  'dashboard.slotGridStatus': 'Status da Grade de Slots (16)',
  'dashboard.assignLot': 'Atribuir Lote',
  'dashboard.modeConfig': 'Configurar Modo',
  'dashboard.none': 'NENHUM',
  'dashboard.unassigned': 'Sem atribuição',

  'models.title': 'Biblioteca de Modelos de Bateria',
  'models.subtitle': 'Capacidade, tensão e limites de qualidade usados para classificação no laboratório.',
  'models.closeLot': 'Fechar Lote',
  'models.newModel': 'Novo Modelo',
  'models.configuredModels': 'Modelos Configurados',
  'models.approvalThresholds': 'Limites de Aprovação',
  'models.environment': 'Ambiente',
  'models.local': 'Local',
  'models.loading': 'Carregando modelos de bateria...',
  'models.loadError': 'Falha ao carregar modelos de bateria.',
  'models.batteryModel': 'Modelo de bateria',
  'models.voltage': 'Tensão',
  'models.capacity': 'Capacidade',
  'models.approvalThreshold': 'Limite de aprovação',
  'models.rejectionThreshold': 'Limite de rejeição',
  'models.traceabilityConfig': 'Configuração de rastreabilidade',

  'reports.title': 'Registros de Testes de Bateria',
  'reports.subtitle': 'Resultados de testes rastreáveis, histórico de slots e registros de qualidade exportáveis.',
  'reports.searchPlaceholder': 'Buscar número de série global...',
  'reports.dateRange': 'Período',
  'reports.datePlaceholder': 'dd/mm/aaaa',
  'reports.startDate': 'Data inicial',
  'reports.endDate': 'Data final',
  'reports.status': 'Status',
  'reports.allStatuses': 'Todos os Status',
  'reports.equipment': 'Equipamento',
  'reports.allEquipment': 'Qualquer equipamento',
  'reports.client': 'Cliente',
  'reports.exportCsv': 'Exportar CSV',
  'reports.applyFilters': 'Aplicar Filtros',
  'reports.clearFilters': 'Limpar Filtros',
  'reports.loading': 'Carregando registros de teste...',
  'reports.loadError': 'Falha ao carregar resultados de teste.',
  'reports.empty': 'Nenhum registro de teste corresponde aos filtros selecionados.',
  'reports.serial': 'Serial',
  'reports.model': 'Modelo',
  'reports.demoPack': 'Pacote Demo 2500mAh',
  'reports.measured': 'Medido',
  'reports.configured': 'Configurado',
  'reports.health': 'Saúde %',
  'reports.internalResistance': 'RI (mOhm)',
  'reports.showing': 'Exibindo',
  'reports.of': 'de',
  'reports.entries': 'entradas',
  'reports.previous': 'Anterior',
  'reports.next': 'Próximo',
  'reports.notAvailable': 'N/D',
};

const es: Record<TranslationKey, string> = {
  'app.brand': 'ConnectDevice',
  'app.platform': 'Plataforma IoT de Gestión de Baterías',
  'app.shortPlatform': 'Gestión IoT de Baterías',
  'app.localDevelopment': 'Desarrollo Local',
  'app.localEnvironment': 'Entorno de desarrollo local',
  'app.demoClient': 'Demo ConnectDevice',
  'app.notAvailableYet': '{label} aún no está disponible',

  'language.label': 'Idioma',
  'language.english': 'Inglés',
  'language.portuguese': 'Portugués',
  'language.spanish': 'Español',

  'nav.dashboard': 'Panel',
  'nav.liveTesting': 'Pruebas en Vivo',
  'nav.batchMgmt': 'Gestión de Lotes',
  'nav.labTools': 'Herramientas de Lab',
  'nav.disposition': 'Disposición',
  'nav.analytics': 'Analítica',
  'nav.admin': 'Admin',
  'nav.overview': 'Resumen',
  'nav.testing': 'Pruebas',
  'nav.lots': 'Lotes',
  'nav.equipment': 'Equipos',
  'nav.reports': 'Reportes',
  'nav.support': 'Soporte',
  'nav.logout': 'Cerrar sesión',
  'nav.scanNewSerial': 'Escanear Nuevo Serial',
  'nav.notifications': 'Notificaciones',
  'nav.settings': 'Configuración',

  'pageTitle.batteryTests': 'Registros de Pruebas de Batería',
  'pageTitle.batteryModels': 'Biblioteca de Modelos de Batería',
  'pageTitle.dashboard': 'Panel',

  'auth.loading': 'Cargando consola...',
  'auth.signIn': 'Iniciar sesión',
  'auth.signingIn': 'Iniciando sesión...',
  'auth.username': 'Usuario',
  'auth.password': 'Contraseña',
  'auth.invalidCredentials': 'Usuario o contraseña inválidos.',
  'auth.validation.usernameRequired': 'El usuario es obligatorio',
  'auth.validation.passwordRequired': 'La contraseña es obligatoria',
  'auth.sessionExpired': 'Tu sesión expiró. Inicia sesión nuevamente.',
  'auth.operatorFallback': 'Operador',

  'status.online': 'ONLINE',
  'status.offline': 'OFFLINE',
  'status.good': 'BUENA',
  'status.half': 'MEDIA',
  'status.defect': 'DEFECTO',
  'status.recarga': 'RECARGA',
  'status.approved': 'APROBADO',
  'status.failed': 'FALLÓ',
  'status.retest': 'REPRUEBA',
  'status.recharge': 'RECARGA',
  'status.idle': 'INACTIVO',
  'status.analyzing': 'ANALIZANDO',

  'dashboard.title': 'Panel General del Laboratorio',
  'dashboard.subtitle': 'Inteligencia de calidad de baterías y telemetría de equipos en tiempo real.',
  'dashboard.currentLoad': 'Carga Actual: 84%',
  'dashboard.systemsNominal': 'Sistemas Nominales',
  'dashboard.totalTests': 'Total de Pruebas (24h)',
  'dashboard.totalTestsTrend': '+12% vs semana pasada',
  'dashboard.passRate': 'Tasa de Aprobación (BUENA %)',
  'dashboard.passRateTrend': 'Meta: 98%',
  'dashboard.processingCount': 'En Procesamiento',
  'dashboard.processingTrend': 'Cola activa',
  'dashboard.onlineEquipment': 'Equipos Online',
  'dashboard.onlineEquipmentTrend': '93,7% activos',
  'dashboard.throughput': 'Rendimiento de Baterías en 24h',
  'dashboard.hourly': 'Por Hora',
  'dashboard.daily': 'Diario',
  'dashboard.loadingTelemetry': 'Cargando telemetría...',
  'dashboard.yieldSegmentation': 'Segmentación de Rendimiento',
  'dashboard.goodLabel': 'BUENA',
  'dashboard.halfLabel': 'MEDIA (Categoría B)',
  'dashboard.defectLabel': 'DEFECTO (Descarte)',
  'dashboard.recargaLabel': 'RECARGA (Reprueba)',
  'dashboard.traceability': 'Trazabilidad garantizada para las 4 clasificaciones.',
  'dashboard.stationSlots': 'Slots de la Estación',
  'dashboard.active': 'Activo',
  'dashboard.done': 'Completado',
  'dashboard.fault': 'Falla',
  'dashboard.linkedHardware': 'Hardware Vinculado',
  'dashboard.activeStation': 'Estación activa',
  'dashboard.maintenanceQueue': 'Cola de mantenimiento',
  'dashboard.recentAnalyses': 'Análisis Recientes de Batería',
  'dashboard.recentAnalysesSubtitle': 'Mostrando los resultados de calidad trazables más recientes',
  'dashboard.viewAllLogs': 'Ver Todos los Registros',
  'dashboard.serialNumber': 'Número de Serie',
  'dashboard.healthSoh': 'Salud (SOH)',
  'dashboard.timestamp': 'Fecha/Hora',
  'dashboard.liveEquipmentStatus': 'Estado de Equipos en Vivo',
  'dashboard.viewFullAnalytics': 'Ver Analítica Completa',
  'dashboard.livePerformanceHeatmap': 'Mapa de Calor de Rendimiento en Vivo',
  'dashboard.heatmapDescription': 'Se detectaron fluctuaciones térmicas y de voltaje en el equipo de demostración local. Un slot sin asignación muestra anomalías de alta resistencia.',
  'dashboard.temp': 'Temp',
  'dashboard.volt': 'Volt',
  'dashboard.anomalyQueue': 'Cola de Detección de Anomalías',
  'dashboard.voltageSag': 'Caída de Voltaje Detectada',
  'dashboard.capacitanceOutOfRange': 'Capacitancia Fuera de Rango',
  'dashboard.minorThermalDrift': 'Deriva Térmica Menor',
  'dashboard.runDiagnostics': 'Ejecutar Diagnóstico del Sistema',
  'dashboard.analyse': 'Analizar',
  'dashboard.currentLot': 'Lote Actual',
  'dashboard.lastActive': 'Última Actividad',
  'dashboard.lastActiveDemo': 'hace 2h 14min',
  'dashboard.runtime': 'Tiempo Activo',
  'dashboard.system': 'SISTEMA',
  'dashboard.slot': 'SLOT',
  'dashboard.throughputCounters': 'Contadores de Rendimiento',
  'dashboard.proc': 'PROC',
  'dashboard.fail': 'FALLA',
  'dashboard.slotGridStatus': 'Estado de la Cuadrícula de Slots (16)',
  'dashboard.assignLot': 'Asignar Lote',
  'dashboard.modeConfig': 'Configurar Modo',
  'dashboard.none': 'NINGUNO',
  'dashboard.unassigned': 'Sin asignación',

  'models.title': 'Biblioteca de Modelos de Batería',
  'models.subtitle': 'Capacidad, voltaje y umbrales de calidad usados para clasificación en laboratorio.',
  'models.closeLot': 'Cerrar Lote',
  'models.newModel': 'Nuevo Modelo',
  'models.configuredModels': 'Modelos Configurados',
  'models.approvalThresholds': 'Umbrales de Aprobación',
  'models.environment': 'Entorno',
  'models.local': 'Local',
  'models.loading': 'Cargando modelos de batería...',
  'models.loadError': 'Error al cargar modelos de batería.',
  'models.batteryModel': 'Modelo de batería',
  'models.voltage': 'Voltaje',
  'models.capacity': 'Capacidad',
  'models.approvalThreshold': 'Umbral de aprobación',
  'models.rejectionThreshold': 'Umbral de rechazo',
  'models.traceabilityConfig': 'Configuración de trazabilidad',

  'reports.title': 'Registros de Pruebas de Batería',
  'reports.subtitle': 'Resultados de pruebas trazables, historial de slots y registros de calidad exportables.',
  'reports.searchPlaceholder': 'Buscar número de serie global...',
  'reports.dateRange': 'Rango de Fechas',
  'reports.datePlaceholder': 'dd/mm/aaaa',
  'reports.startDate': 'Fecha inicial',
  'reports.endDate': 'Fecha final',
  'reports.status': 'Estado',
  'reports.allStatuses': 'Todos los Estados',
  'reports.equipment': 'Equipo',
  'reports.allEquipment': 'Cualquier equipo',
  'reports.client': 'Cliente',
  'reports.exportCsv': 'Exportar CSV',
  'reports.applyFilters': 'Aplicar Filtros',
  'reports.clearFilters': 'Limpiar Filtros',
  'reports.loading': 'Cargando registros de prueba...',
  'reports.loadError': 'Error al cargar resultados de prueba.',
  'reports.empty': 'Ningún registro de prueba coincide con los filtros seleccionados.',
  'reports.serial': 'Serial',
  'reports.model': 'Modelo',
  'reports.demoPack': 'Paquete Demo 2500mAh',
  'reports.measured': 'Medido',
  'reports.configured': 'Configurado',
  'reports.health': 'Salud %',
  'reports.internalResistance': 'RI (mOhm)',
  'reports.showing': 'Mostrando',
  'reports.of': 'de',
  'reports.entries': 'entradas',
  'reports.previous': 'Anterior',
  'reports.next': 'Siguiente',
  'reports.notAvailable': 'N/D',
};

const languageLabels: Record<Language, TranslationKey> = {
  en: 'language.english',
  'pt-BR': 'language.portuguese',
  es: 'language.spanish',
};

const localeByLanguage: Record<Language, string> = {
  en: 'en-US',
  'pt-BR': 'pt-BR',
  es: 'es-ES',
};

const STORAGE_KEY = 'connectdevice.language';

export const resources = {
  'pt-BR': {
    translation: pt,
  },
  en: {
    translation: en,
  },
  es: {
    translation: es,
  },
} as const;

interface I18nContextValue {
  language: Language;
  languages: Language[];
  locale: string;
  languageLabels: Record<Language, TranslationKey>;
  setLanguage: (language: Language) => void;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
}

function isLanguage(value: string | null): value is Language {
  return value === 'pt-BR' || value === 'en' || value === 'es';
}

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'pt-BR';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'pt') {
    window.localStorage.setItem(STORAGE_KEY, 'pt-BR');
    return 'pt-BR';
  }
  return isLanguage(stored) ? stored : 'pt-BR';
}

if (!i18next.isInitialized) {
  void i18next.use(initReactI18next).init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: 'pt-BR',
    supportedLngs: ['pt-BR', 'en', 'es'],
    ns: ['translation'],
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });
}

function normalizeLanguage(language: string): Language {
  if (language.startsWith('pt')) return 'pt-BR';
  if (language.startsWith('es')) return 'es';
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export function useI18n(): I18nContextValue {
  const { i18n, t } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

  return {
    language,
    languages: ['pt-BR', 'en', 'es'],
    locale: localeByLanguage[language],
    languageLabels,
    setLanguage: (nextLanguage) => {
      window.localStorage.setItem(STORAGE_KEY, nextLanguage);
      void i18n.changeLanguage(nextLanguage);
    },
    t: (key, values) => (values ? t(key, values) : t(key)),
  };
}
